import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import type {
  ActionBarAction,
  ActionBarConfig,
} from '@/lib/types/action-bar.types';

type ActionBarContextValue = {
  config: ActionBarConfig | null;
  setConfig: (config: ActionBarConfig | null) => void;
};

const ActionBarContext = createContext<ActionBarContextValue | undefined>(
  undefined
);

/**
 * Compare two action arrays by their non-function properties.
 * Ignores onClick since functions are recreated each render.
 */
function areActionsEqual(
  prev: ActionBarAction[],
  next: ActionBarAction[]
): boolean {
  if (prev.length !== next.length) return false;
  return prev.every((action, i) => {
    const other = next[i];
    return (
      action.key === other.key &&
      action.label === other.label &&
      action.variant === other.variant &&
      action.disabled === other.disabled &&
      action.loading === other.loading &&
      action.fullWidth === other.fullWidth
    );
  });
}

/**
 * Compare two configs by their meaningful properties (ignoring function references).
 * This prevents infinite renders when only onClick handlers are recreated.
 */
function isConfigEqual(
  prev: ActionBarConfig | null,
  next: ActionBarConfig | null
): boolean {
  if (prev === next) return true;
  if (!prev || !next) return false;

  return (
    prev.title === next.title &&
    prev.visible === next.visible &&
    prev.className === next.className &&
    areActionsEqual(prev.actions, next.actions)
  );
}

export function ActionBarProvider({ children }: { children: ReactNode }) {
  const [config, setConfig] = useState<ActionBarConfig | null>(null);

  const value = useMemo(
    () => ({
      config,
      setConfig,
    }),
    [config]
  );

  return (
    <ActionBarContext.Provider value={value}>
      {children}
    </ActionBarContext.Provider>
  );
}

export function useActionBarContext() {
  const ctx = useContext(ActionBarContext);
  if (!ctx) {
    throw new Error(
      'useActionBarContext must be used within ActionBarProvider'
    );
  }
  return ctx;
}

/**
 * Convenience hook to register an action bar for the lifetime of a component.
 * Uses deep comparison of config properties (excluding functions) to prevent
 * infinite render loops when config objects are recreated with new function references.
 */
export function useActionBar(config: ActionBarConfig | null) {
  const { setConfig } = useActionBarContext();
  const prevConfigRef = useRef<ActionBarConfig | null>(null);

  // Use useLayoutEffect to update config synchronously before paint
  // This ensures the ActionBar is visible immediately
  useLayoutEffect(() => {
    if (!isConfigEqual(prevConfigRef.current, config)) {
      prevConfigRef.current = config;
      setConfig(config);
    }
  }, [config, setConfig]);

  // Cleanup only on unmount - separate effect with empty deps
  useEffect(() => {
    return () => {
      // Clear both the config AND prevConfigRef on unmount
      // This ensures that when React Strict Mode remounts the component,
      // the useLayoutEffect will see isEqual: false and properly re-set the config
      prevConfigRef.current = null;
      setConfig(null);
    };
  }, [setConfig]);
}

export function useSetActionBar() {
  const { setConfig } = useActionBarContext();
  return useCallback(
    (config: ActionBarConfig | null) => {
      setConfig(config);
    },
    [setConfig]
  );
}

